import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type { CloudTrailEvent, AccountMeta, SecurityAlert, LlmUsageStats } from './types';

export const awsCloudTrailApiRef = createApiRef<AwsCloudTrailApi>({
  id: 'plugin.aws-cloudtrail-monitor.service',
});

export interface AwsCloudTrailApi {
  getAccounts(): Promise<AccountMeta[]>;
  getEvents(options: {
    startDate?: string;
    endDate?: string;
    accounts?: string[];
  }): Promise<CloudTrailEvent[]>;
  downloadReport(date: string): Promise<Blob>;
  getSecurityAlerts(): Promise<SecurityAlert[]>;
  getLlmUsageStats(): Promise<LlmUsageStats>;
  updateBudget(budgetUsd: number): Promise<void>;
}

export class AwsCloudTrailClient implements AwsCloudTrailApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('aws-cloudtrail-monitor');
  }

  async getAccounts(): Promise<AccountMeta[]> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/cloudtrail/accounts`);
    if (!resp.ok) return [];
    const data = await resp.json();
    return data.accounts as AccountMeta[];
  }

  async getEvents(options: {
    startDate?: string;
    endDate?: string;
    accounts?: string[];
  }): Promise<CloudTrailEvent[]> {
    const base   = await this.baseUrl();
    const params = new URLSearchParams();
    if (options.startDate) params.set('startDate', options.startDate);
    if (options.endDate)   params.set('endDate', options.endDate);
    if (options.accounts && options.accounts.length > 0) {
      params.set('accounts', options.accounts.join(','));
    }
    const resp = await this.fetchApi.fetch(
      `${base}/cloudtrail/events?${params.toString()}`,
    );
    if (!resp.ok) {
      throw new Error(`CloudTrail API error: ${resp.statusText}`);
    }
    const data = await resp.json();
    return data.events as CloudTrailEvent[];
  }

  async downloadReport(date: string): Promise<Blob> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/cloudtrail/report?date=${date}`);
    if (!resp.ok) throw new Error(`Error al generar el reporte: ${resp.statusText}`);
    return resp.blob();
  }

  async getSecurityAlerts(): Promise<SecurityAlert[]> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/cloudtrail/security-alerts/history`);
    if (!resp.ok) return [];
    const data = await resp.json();
    return data.alerts as SecurityAlert[];
  }

  async updateBudget(budgetUsd: number): Promise<void> {
    const base = await this.baseUrl();
    await this.fetchApi.fetch(`${base}/cloudtrail/llm-usage/budget`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ budgetUsd }),
    });
  }

  async getLlmUsageStats(): Promise<LlmUsageStats> {
    const base = await this.baseUrl();
    const resp = await this.fetchApi.fetch(`${base}/cloudtrail/llm-usage/stats`);
    if (!resp.ok) return { budgetUsd: 1.00, totals: null, byAccount: [], byDay: [] };
    return resp.json();
  }
}
